import {
  Button,
  Grid,
  MenuItem,
  TextField,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
} from '@mui/material'
import { Form, Formik } from 'formik'
import * as Yup from 'yup'
import { memo, useCallback, useEffect, useState } from 'react'
import { isEqual } from 'lodash'
import CurrencyField from '../../../../../components/CurrencyField/CurrencyField'
import { metodoPrograma } from '../../../../../components/api'
import dayjs from 'dayjs'

const validationSchema = Yup.object({
  tpLancamento: Yup.string().required('Tipo é obrigatório'),
  idFormaPagamento: Yup.number().required('Forma de pagamento é obrigatória'),
  dtLancamento: Yup.string().required('Data é obrigatória'),
  vlLancamento: Yup.number()
    .required('Valor é obrigatório')
    .positive('Valor deve ser positivo'),
  dsHistorico: Yup.string()
    .required('Descrição é obrigatória')
    .max(255, 'A descrição deve ter no máximo 255 caracteres'),
})

const ModalLancamentoCaixa = ({ data, close }) => {
  const [listaFormaPagamento, setListaFormaPagamento] = useState([])

  const listaFormas = useCallback(() => {
    metodoPrograma('listaFormaPagamento', {
      idRegistro: data?.idRegistro,
    }).then((res) => {
      if (res.status) {
        setListaFormaPagamento(res.lista)
      }
    })
  }, [data?.idRegistro])

  useEffect(() => {
    listaFormas()
  }, [listaFormas])

  const onSubmit = async (values, { setSubmitting }) => {
    const payload = {
      idCaixa: data?.idCaixa,
      idLocalAtendimento: data?.idLocalAtendimento,
      idRegistro: data?.idRegistro,
      tpLancamento: values.tpLancamento,
      vlLancamento: values.vlLancamento,
      dsHistorico: values.dsHistorico,
      dtLancamento: values.dtLancamento
        ? dayjs(values.dtLancamento).format('YYYY-MM-DD HH:mm')
        : undefined,
      idFormaPagamento: values.idFormaPagamento,
    }

    const res = await metodoPrograma('insereLancamentoCaixa', payload)
    setSubmitting(false)
    if (res?.status) {
      close(true)
    }
  }

  return (
    <Formik
      initialValues={{
        tpLancamento: 'E',
        idFormaPagamento: '',
        dtLancamento: dayjs().format('YYYY-MM-DD'),
        vlLancamento: '',
        dsHistorico: '',
      }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({
        isSubmitting,
        submitForm,
        values,
        setFieldValue,
        setFieldTouched,
        errors,
        touched,
      }) => (
        <Form>
          <Dialog
            open={true}
            onClose={() => close(false)}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>Lançamento de Caixa</DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <TextField
                    name="tpLancamento"
                    label="Tipo"
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    size="small"
                    select
                    value={values.tpLancamento}
                    onChange={(e) =>
                      setFieldValue('tpLancamento', e.target.value)
                    }
                    onBlur={() => setFieldTouched('tpLancamento', true)}
                    error={touched.tpLancamento && !!errors.tpLancamento}
                    helperText={touched.tpLancamento && errors.tpLancamento}
                  >
                    <MenuItem value={'E'}>Entrada</MenuItem>
                    <MenuItem value={'S'}>Saída</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={8}>
                  <TextField
                    name="idFormaPagamento"
                    label="Forma de Pagamento"
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    size="small"
                    select
                    value={values.idFormaPagamento}
                    onChange={(e) =>
                      setFieldValue('idFormaPagamento', e.target.value)
                    }
                    onBlur={() => setFieldTouched('idFormaPagamento', true)}
                    error={
                      touched.idFormaPagamento && !!errors.idFormaPagamento
                    }
                    helperText={
                      touched.idFormaPagamento && errors.idFormaPagamento
                    }
                  >
                    {listaFormaPagamento.map((option) => (
                      <MenuItem key={option[0]} value={option[0]}>
                        {option[1]}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    name="dtLancamento"
                    label="Data"
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    size="small"
                    type="date"
                    value={values.dtLancamento}
                    onChange={(e) =>
                      setFieldValue('dtLancamento', e.target.value)
                    }
                    onBlur={() => setFieldTouched('dtLancamento', true)}
                    error={touched.dtLancamento && !!errors.dtLancamento}
                    helperText={touched.dtLancamento && errors.dtLancamento}
                  />
                </Grid>
                <Grid item xs={6}>
                  <CurrencyField
                    label="Valor"
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    size="small"
                    value={values.vlLancamento}
                    onChange={(v) => {
                      setFieldValue('vlLancamento', v)
                      setFieldTouched('vlLancamento', true)
                    }}
                    error={touched.vlLancamento && !!errors.vlLancamento}
                    helperText={touched.vlLancamento && errors.vlLancamento}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="dsHistorico"
                    label="Descrição"
                    fullWidth
                    multiline
                    minRows={3}
                    margin="dense"
                    variant="outlined"
                    size="small"
                    value={values.dsHistorico}
                    onChange={(e) =>
                      setFieldValue('dsHistorico', e.target.value)
                    }
                    onBlur={() => setFieldTouched('dsHistorico', true)}
                    error={touched.dsHistorico && !!errors.dsHistorico}
                    helperText={touched.dsHistorico && errors.dsHistorico}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => close(false)}
                color="secondary"
                variant="contained"
                style={{ margin: '5px' }}
              >
                Cancelar
              </Button>
              <Button
                onClick={submitForm}
                disabled={isSubmitting}
                variant="contained"
                color="primary"
                style={{ margin: '5px' }}
              >
                Salvar
              </Button>
            </DialogActions>
          </Dialog>
        </Form>
      )}
    </Formik>
  )
}

export default memo(ModalLancamentoCaixa, isEqual)
